import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable, FormTextField, FormDatePicker, FormSelect } from '../../shared';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { formatHotelDate } from '../../utils/dateUtils';
import EventBlockDrawer from '../inventory/EventBlockDrawer';
import {
  fetchAllInventoryBlocks,
  createInventoryBlock,
  updateInventoryBlock,
  deleteInventoryBlock,
  setInventoryBlockActive,
  getInventoryBlockErrorMessage,
  defaultInventoryBlockStartTime,
  defaultInventoryBlockEndTime,
  parseInventoryBlockTime,
  formatInventoryBlockTime,
} from '../../services/inventoryBlockService';
import { getConfigurationRoomTypes, getChannels, getInventoryData } from '../../services/api';
import {
  DEFAULT_RATE_CHANNEL_SELECTION,
  deriveBlockChannelSelection,
  expandChannelSelection,
} from '../common/AllChannelsSelector';
import {
  getDistributionSectionPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionTitleSx,
  getDistributionSectionSubtitleSx,
  getDistributionActionButtonSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';

const THEME_COLOR = THEME_COLORS.PRIMARY;

const BLOCK_TYPE_OPTIONS = [
  { value: 'EVENT', label: 'Event' },
  { value: 'OVERBOOKING', label: 'Overbooking Protection' },
  { value: 'MANUAL', label: 'Manual Block' },
  { value: 'ENQUIRY', label: 'Enquiry' },
  { value: 'OTHER', label: 'Other' },
];

const BLOCK_TYPE_COLORS = {
  MAINTENANCE: '#f59e0b',
  EVENT: '#3b82f6',
  OVERBOOKING: '#ef4444',
  MANUAL: THEME_COLOR,
  ENQUIRY: '#14b8a6',
  OTHER: '#a78bfa',
};

const createDefaultFilters = () => ({
  room_type_id: '',
  block_type: '',
  active_on: '',
  reason: '',
  status: '',
});

const DEFAULT_SORT = { column: 'created_at', direction: 'desc' };

const createDefaultBlockWindow = () => {
  const start = moment().seconds(0).milliseconds(0);
  return {
    start_date: start.clone(),
    end_date: start.clone().add(1, 'day'),
    start_time: start.clone(),
    end_time: start.clone(),
  };
};

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function sortBlocks(blocks, sortConfig) {
  const { column, direction } = sortConfig;
  if (!column) return blocks;
  const sorted = [...blocks].sort((x, y) => {
    let a = x[column];
    let b = y[column];
    if (column === 'is_active') {
      a = x.is_active ? 1 : 0;
      b = y.is_active ? 1 : 0;
    }
    return compareValues(a, b);
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}

function filterBlocks(blocks, filters) {
  return blocks.filter((block) => {
    if (filters.room_type_id && String(block.room_type_id) !== String(filters.room_type_id)) {
      return false;
    }
    if (filters.block_type && block.block_type !== filters.block_type) {
      return false;
    }
    if (filters.status === 'active' && !block.is_active) return false;
    if (filters.status === 'inactive' && block.is_active) return false;
    if (filters.active_on) {
      // Inclusive block dates: start_date <= day <= end_date
      if (!block.start_date || !block.end_date) return false;
      if (filters.active_on < block.start_date || filters.active_on > block.end_date) {
        return false;
      }
    }
    if (filters.reason) {
      const needle = filters.reason.trim().toLowerCase();
      if (needle && !String(block.reason || '').toLowerCase().includes(needle)) {
        return false;
      }
    }
    return true;
  });
}

const InventoryBlockSection = () => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);
  const [blocks, setBlocks] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState(() => createDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ ...DEFAULT_SORT });
  const [pagination, setPagination] = useState({ page: 0, perPage: 10, totalRecords: 0 });
  const [formData, setFormData] = useState({
    room_type_id: '',
    block_type: 'MANUAL',
    ...createDefaultBlockWindow(),
    num_rooms: 1,
    reason: '',
    is_active: true,
    selectedChannels: [...DEFAULT_RATE_CHANNEL_SELECTION],
    selected_room_ids: [],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
    fetchChannels();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [blocksData, roomTypesData] = await Promise.all([
        fetchAllInventoryBlocks(),
        getConfigurationRoomTypes(),
      ]);
      setBlocks(blocksData.blocks || []);
      setRoomTypes(roomTypesData || []);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load inventory blocks. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const channelsData = await getChannels();
      // getChannels returns { channels: [...] } or the data directly
      const channelsArray = channelsData?.channels || (Array.isArray(channelsData) ? channelsData : []);setChannels(channelsArray);
    } catch (error) {setChannels([]);
    }
  };

  // Client-side filter + sort + paginate (list API returns all blocks)
  const filteredSortedBlocks = useMemo(
    () => sortBlocks(filterBlocks(blocks, filters), sortConfig),
    [blocks, filters, sortConfig]
  );

  const pagedBlocks = useMemo(() => {
    const start = pagination.page * pagination.perPage;
    return filteredSortedBlocks.slice(start, start + pagination.perPage);
  }, [filteredSortedBlocks, pagination.page, pagination.perPage]);

  useEffect(() => {
    setPagination((prev) => {
      const totalRecords = filteredSortedBlocks.length;
      const maxPage = Math.max(0, Math.ceil(totalRecords / prev.perPage) - 1);
      return {
        ...prev,
        totalRecords,
        page: Math.min(prev.page, maxPage),
      };
    });
  }, [filteredSortedBlocks]);

  // Manage Event drawer tab: blocks covering today (including disabled, for re-enable)
  const activeTodayBlocks = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    return blocks.filter(
      (block) =>
        block.start_date &&
        block.end_date &&
        block.start_date <= today &&
        block.end_date >= today
    );
  }, [blocks]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination((prev) => ({ ...prev, perPage: newPerPage, page: 0 }));
  };

  const handleOpenDrawer = (block = null) => {
    if (block) {
      setEditingBlock(block);
      setFormData({
        room_type_id: block.room_type_id || '',
        block_type: block.block_type || 'MANUAL',
        start_date: block.start_date ? moment(block.start_date) : null,
        end_date: block.end_date ? moment(block.end_date) : null,
        start_time: parseInventoryBlockTime(block.start_time) || defaultInventoryBlockStartTime(),
        end_time: parseInventoryBlockTime(block.end_time) || defaultInventoryBlockEndTime(),
        num_rooms: block.num_rooms || 1,
        reason: block.reason || '',
        is_active: block.is_active !== undefined ? block.is_active : true,
        selectedChannels: deriveBlockChannelSelection(block),
        selected_room_ids: Array.isArray(block.room_ids)
          ? block.room_ids.map((id) => Number(id)).filter(Boolean)
          : [],
      });
    } else {
      setEditingBlock(null);
      const defaultWindow = createDefaultBlockWindow();
      setFormData({
        room_type_id: roomTypes.length > 0 ? roomTypes[0].id : '',
        block_type: 'MANUAL',
        ...defaultWindow,
        num_rooms: 1,
        reason: '',
        is_active: true,
        selectedChannels: [...DEFAULT_RATE_CHANNEL_SELECTION],
        selected_room_ids: [],
      });
    }
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingBlock(null);
    setFormData({
      room_type_id: '',
      block_type: 'MANUAL',
      ...createDefaultBlockWindow(),
      num_rooms: 1,
      reason: '',
      is_active: true,
      selectedChannels: [...DEFAULT_RATE_CHANNEL_SELECTION],
      selected_room_ids: [],
    });
    setErrors({});
    setErrorMessage(null);
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.room_type_id) {
      newErrors.room_type_id = 'Room type is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && formData.end_date.isBefore(formData.start_date, 'day')) {
      newErrors.end_date = 'End date must be after or equal to start date';
    }
    const blockStartTime = parseInventoryBlockTime(formData.start_time);
    const blockEndTime = parseInventoryBlockTime(formData.end_time);
    if (!blockStartTime) {
      newErrors.start_time = 'Block time is required';
    }
    if (!blockEndTime) {
      newErrors.end_time = 'Unblock time is required';
    }
    if (
      blockStartTime &&
      blockEndTime &&
      formData.start_date &&
      formData.end_date &&
      formData.end_date.isSame(formData.start_date, 'day') &&
      !blockEndTime.isAfter(blockStartTime)
    ) {
      newErrors.end_time = 'Unblock time must be after block time on the same day';
    }
    if (!formData.selectedChannels || formData.selectedChannels.length === 0) {
      newErrors.selectedChannels = 'At least one channel must be selected';
    }
    const selectedRoomIds = Array.isArray(formData.selected_room_ids)
      ? formData.selected_room_ids.map((id) => Number(id)).filter(Boolean)
      : [];
    if (selectedRoomIds.length === 0) {
      newErrors.selected_room_ids = 'Select at least one room to block';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    try {
      // Expand All Channels into concrete OTA ids
      const expanded = expandChannelSelection(formData.selectedChannels || [], channels);
      const channelIds = expanded
        .filter((id) => id !== 'pms')
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      
      const dataToSend = {
        room_type_id: formData.room_type_id,
        block_type: formData.block_type,
        start_date: formData.start_date ? formData.start_date.format('YYYY-MM-DD') : null,
        end_date: formData.end_date ? formData.end_date.format('YYYY-MM-DD') : null,
        start_time: formatInventoryBlockTime(formData.start_time),
        end_time: formatInventoryBlockTime(formData.end_time),
        num_rooms: selectedRoomIds.length,
        reason: formData.reason || null,
        is_active: formData.is_active,
        channel_ids: channelIds,
        include_pms: (formData.selectedChannels || []).includes('pms'),
        room_ids: selectedRoomIds,
      };

      if (editingBlock) {
        await updateInventoryBlock(editingBlock.id, dataToSend);
        setSnackbar({
          open: true,
          message: 'Inventory block updated successfully',
          severity: 'success',
        });
      } else {
        await createInventoryBlock(dataToSend);
        setSnackbar({
          open: true,
          message: 'Inventory block created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchData();
    } catch (error) {if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        setErrors(serverErrors);
        const firstError = Object.values(serverErrors).flat().find(Boolean);
        if (firstError) {
          setErrorMessage(String(firstError));
        }
      } else {
        setErrorMessage(error.response?.data?.error || 'Failed to save inventory block');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (block) => {
    setBlockToDelete(block);
    setDeleteDialogOpen(true);
  };

  const handleToggleActive = async (block) => {
    if (!block?.id) return;
    setIsSaving(true);
    try {
      const nextActive = !block.is_active;
      await setInventoryBlockActive(block, nextActive);
      setSnackbar({
        open: true,
        message: nextActive
          ? 'Inventory block enabled — inventory recalculated'
          : 'Inventory block disabled — inventory recalculated',
        severity: 'success',
      });
      await fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getInventoryBlockErrorMessage(error, 'Failed to update inventory block status'),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!blockToDelete) return;

    setIsSaving(true);
    try {
      await deleteInventoryBlock(blockToDelete.id);
      setSnackbar({
        open: true,
        message: 'Inventory block deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setBlockToDelete(null);
      fetchData();
    } catch (error) {setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete inventory block',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const findChannelName = useCallback(
    (channelId) => channels.find((ch) => Number(ch.id) === Number(channelId))?.name,
    [channels]
  );

  const columns = [
    {
      field: 'room_type_name',
      label: 'Room Type',
      sortable: true,
      render: (value, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value || `Room Type ${row.room_type_id}`}
          </Typography>
          {Array.isArray(row.rooms) && row.rooms.length > 0 && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
              Rooms: {row.rooms.map((r) => r.room_number).join(', ')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'block_type',
      label: 'Type',
      sortable: true,
      render: (value, row) => {
        const color = BLOCK_TYPE_COLORS[value] || THEME_COLOR;
        return (
          <Chip
            label={row.block_type_display || value}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: alpha(color, 0.14),
              color,
              border: `1px solid ${alpha(color, 0.3)}`,
            }}
          />
        );
      },
    },
    {
      field: 'start_date',
      label: 'From',
      sortable: true,
      render: (value) => (value ? formatHotelDate(value) : '—'),
    },
    {
      field: 'end_date',
      label: 'To',
      sortable: true,
      render: (value) => (value ? formatHotelDate(value) : '—'),
    },
    {
      field: 'num_rooms',
      label: 'Rooms',
      sortable: true,
      align: 'center',
    },
    {
      field: 'scope',
      label: 'Scope',
      sortable: false,
      render: (_value, row) => {
        const channelIds = Array.isArray(row.channel_ids) ? row.channel_ids : [];
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.include_pms && (
              <Chip label="PMS" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {channelIds.map((channelId) => (
              <Chip
                key={channelId}
                label={findChannelName(channelId) || `Channel ${channelId}`}
                size="small"
                color="secondary"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            ))}
            {!row.include_pms && channelIds.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'reason',
      label: 'Reason',
      sortable: false,
      render: (value) =>
        value ? (
          <Tooltip title={value}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
              {value}
            </Typography>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      field: 'is_active',
      label: 'Status',
      sortable: true,
      render: (_value, row) => (
        <Chip
          label={row.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={row.is_active ? 'success' : 'default'}
          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_value, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDrawer(row);
              }}
              sx={{ color: THEME_COLOR }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              sx={{ color: '#ef4444' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'room_type_name':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.room_type_id}
            onChange={(e) => handleFilterChange('room_type_id', e.target.value)}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All Room Types</MenuItem>
            {roomTypes.map((roomType) => (
              <MenuItem key={roomType.id} value={String(roomType.id)}>
                {roomType.name}
              </MenuItem>
            ))}
          </FormSelect>
        );
      case 'block_type':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.block_type}
            onChange={(e) => handleFilterChange('block_type', e.target.value)}
            displayEmpty
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {BLOCK_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </FormSelect>
        );
      case 'start_date':
        return (
          <FormDatePicker
            key={column.field}
            label="Active on"
            value={filters.active_on ? moment(filters.active_on) : null}
            onChange={(newValue) =>
              handleFilterChange('active_on', newValue ? newValue.format('YYYY-MM-DD') : '')
            }
            sx={{ minWidth: 140 }}
          />
        );
      case 'reason':
        return (
          <FormTextField
            key={column.field}
            size="small"
            label="Reason"
            value={filters.reason}
            onChange={(e) => handleFilterChange('reason', e.target.value)}
            sx={{ minWidth: 120 }}
          />
        );
      case 'is_active':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            displayEmpty
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field}></div>;
    }
  });

  return (
    <Box>
      <Paper
        elevation={0}
        sx={getDistributionSectionPaperSx(theme, isMobileLayout)}
      >
        <Box sx={getDistributionSectionHeaderRowSx(isMobileLayout)}>
          <Box sx={getDistributionSectionHeaderLeadSx(isMobileLayout)}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <BlockIcon sx={{ fontSize: getDistributionSectionIconFontSize(isMobileLayout), color: THEME_COLOR }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={getDistributionSectionTitleSx(isMobileLayout)}>
                Inventory Block
              </Typography>
              <Typography variant="body2" sx={getDistributionSectionSubtitleSx(isMobileLayout)}>
                Block inventory for maintenance, events, or other reasons
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={getDistributionActionButtonSx(THEME_COLOR, isMobileLayout)}
          >
            Add Block
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={pagedBlocks}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onSortChange={handleSortChange}
          sortConfig={sortConfig}
          emptyMessage="No inventory blocks found matching the selected filters."
          stickyHeader
          filterRow={filterRowCells}
          skeletonRowCount={5}
        />
      </Paper>

      {/* Event Block Drawer */}
      <EventBlockDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSave}
        isSaving={isSaving}
        errorMessage={errorMessage}
        onErrorMessageChange={setErrorMessage}
        inventoryBlocks={activeTodayBlocks}
        loading={loading}
        onRefresh={fetchData}
        onEdit={(block) => handleOpenDrawer(block)}
        onToggleActive={handleToggleActive}
        formData={formData}
        onFormDataChange={setFormData}
        errors={errors}
        onErrorsChange={setErrors}
        allRoomTypes={roomTypes}
        channels={channels}
        getInventoryData={getInventoryData}
        editingBlock={editingBlock}
        onDeleteConfirm={handleDeleteConfirm}
        deleteDialogOpen={deleteDialogOpen}
        onDeleteDialogClose={() => {
          setDeleteDialogOpen(false);
          setBlockToDelete(null);
        }}
        blockToDelete={blockToDelete}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryBlockSection;
